import axios from '@/common/axios'
import { useMlapProjectStore } from '@/stores/mlapProject'

const GEOGRIDS_URL = '/mlap/ajax/settings/geogrids'
const AUDITS_URL = '/mlap/ajax/settings/audits'
const LSA_ACCOUNTS_URL = '/mlap/ajax/settings/lsa_accounts'

function territoriesUrl(suffix = '') {
  const store = useMlapProjectStore()
  return `${store.ajaxPrefix}/settings/territories${suffix}`
}

function cardsUrl() {
  const store = useMlapProjectStore()
  return `${store.ajaxPrefix}/settings/cards`
}

function lsaAttributionUrl() {
  const store = useMlapProjectStore()
  return `${store.ajaxPrefix}/settings/lsa_attribution`
}

function territoryAttributionUrl() {
  const store = useMlapProjectStore()
  return `${store.ajaxPrefix}/settings/territory_attribution`
}

export function getGeogridConfigs(params) {
  return axios.get(GEOGRIDS_URL, { params })
}

export function toggleGeogridEnabled(id) {
  return axios.patch(`${GEOGRIDS_URL}/${id}/toggle_enabled`)
}

export function deleteGeogridConfig(id) {
  return axios.delete(`${GEOGRIDS_URL}/${id}`)
}

export function getAuditConfigs(params) {
  return axios.get(AUDITS_URL, { params })
}

export function toggleAuditEnabled(id) {
  return axios.patch(`${AUDITS_URL}/${id}/toggle_enabled`)
}

export function getTerritories() {
  return axios.get(territoriesUrl())
}

export function createTerritory(data) {
  return axios.post(territoriesUrl(), data)
}

export function updateTerritory(id, data) {
  return axios.patch(territoriesUrl(`/${id}`), data)
}

export function deleteTerritory(id) {
  return axios.delete(territoriesUrl(`/${id}`))
}

// Blob so the CSV attachment reaches the caller intact and can be handed
// straight to createObjectURL.
export function exportTerritories() {
  return axios.get(territoriesUrl('/export'), { responseType: 'blob' })
}

export function getLsaAccounts() {
  return axios.get(LSA_ACCOUNTS_URL)
}

export function updateLsaAssignment(accountId, organizationsLocationId) {
  return axios.patch(`${LSA_ACCOUNTS_URL}/${accountId}`, {
    organizations_location_id: organizationsLocationId
  })
}

// A territory replaces the location mapping — the server clears whichever side is not set.
export function updateLsaTerritoryAssignment(accountId, mlapTerritoryId) {
  return axios.patch(`${LSA_ACCOUNTS_URL}/${accountId}`, {
    mlap_territory_id: mlapTerritoryId
  })
}

export function updateLsaAttributionMode(accountId, attributionMode) {
  return axios.patch(`${LSA_ACCOUNTS_URL}/${accountId}`, {
    attribution_mode: attributionMode
  })
}

export function getCardVisibility() {
  return axios.get(cardsUrl())
}

export function updateCardVisibility(data) {
  return axios.patch(cardsUrl(), data)
}

export function getLsaAttribution() {
  return axios.get(lsaAttributionUrl())
}

export function updateLsaAttribution(payload) {
  return axios.patch(lsaAttributionUrl(), payload)
}

export function rebuildLsaAccount(accountId) {
  return axios.post(`${LSA_ACCOUNTS_URL}/${accountId}/rebuild`)
}

export function rebuildLsaAttribution() {
  return axios.post(`${lsaAttributionUrl()}/rebuild`)
}

export function getTerritoryAttribution() {
  return axios.get(territoryAttributionUrl())
}

export function updateTerritoryAttribution(enabled) {
  return axios.patch(territoryAttributionUrl(), { enabled })
}

// `mode` is optional: omitting it flips the switch without touching the stored mode.
export function updateTerritoryAllocation(enabled, mode = null) {
  const store = useMlapProjectStore()
  const payload = mode ? { enabled, mode } : { enabled }
  return axios.patch(`${store.ajaxPrefix}/settings/territory_allocation`, payload)
}

// One { key, color } pair per call -- the server merges single keys under a
// row lock so concurrent clients can't clobber each other with stale maps.
export function updateTrendsColor(key, color) {
  const store = useMlapProjectStore()
  return axios.patch(`${store.ajaxPrefix}/settings/trends_colors`, { key, color })
}

// GET twins of the sidebar settings pages' styxie payloads, keyed by the
// page key used in router/routes/mlap.js and the mlapSettings store.
const SETTINGS_PAGE_URLS = {
  integrations: '/mlap/ajax/settings/integrations',
  gscGa4: '/mlap/ajax/settings/gsc_ga4',
  googleAds: '/mlap/ajax/settings/google_ads',
  bingAds: '/mlap/ajax/settings/bing_ads',
  metaAds: '/mlap/ajax/settings/meta_ads',
  nextdoorAds: '/mlap/ajax/settings/nextdoor_ads',
  callRail: '/mlap/ajax/settings/call_rail',
  bing: '/mlap/ajax/settings/bing'
}

export function getSettingsPage(key) {
  return axios.get(SETTINGS_PAGE_URLS[key])
}
